import React from "react";
import { Button, Spacer, VDBText, Input, Container } from "@components/atoms";
import Modal from "@components/atoms/Modal";
import { ScrollView } from "react-native";
import { SpaceType } from "@components/atoms/Spacer/types";
import RadioButton from "@components/atoms/RadioButton";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import { Controller, useForm } from "react-hook-form";
import { schema } from "@components/molecules/JBTdropdown/utils/JBTnumberSchema";
import { yupResolver } from "@hookform/resolvers/yup";
import { JBTdropdownProps, numberType } from "./types";
import RenderHtml from "react-native-render-html";
import { useEffect, useState } from "react";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useJBTConnect, useUpdateUser } from "@apis/user";
import { useTheme } from "styled-components/native";
import { useTranslation } from "react-i18next";
import { showToast, ToastTypes } from "../VDBToast/util";
import { useSelector } from "react-redux";
import JBTConnectedModal from "../JBTConnectedModal";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";

const JBTdropdown = ({
  onClose,
  isOpen,
  HtmlSource,
  styleKey,
}: JBTdropdownProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm({
    resolver: yupResolver(schema),
  });
  const typography = useTypographyStore();
  const theme = useTheme();
  const { deviceWidth: width } = useIsFoldable();
  const [selectedOption, setSelectedOption] = useState(numberType.JBT);
  const [jbtErrorOccurred, setJbtErrorOccurred] = useState(false);
  const [jbtErrorMsg, setJbtErrorMsg] = useState("");
  const { t } = useTranslation();
  const validNumberRegex = /^[0-9+]*$/;
  const JBTConnect = useJBTConnect();
  const installationIdentifier = useSelector(
    (state: any) => state.appState?.installationIdentifier
  );
  const userInfo = useUserInfo();
  const updateUser = useUpdateUser(userInfo.getUserId());
  const jbt_id = userInfo.jbtDetails()?.jbt_id;

  useEffect(() => {
    if (JBTConnect.error) {
      setJbtErrorOccurred(true);
      setJbtErrorMsg(JBTConnect.data?.data.message);
    }
  }, [JBTConnect.error]);

  useEffect(() => {
    if (JBTConnect.isSuccess && JBTConnect.data?.data.connected === false) {
      setJbtErrorOccurred(true);
      setJbtErrorMsg(JBTConnect.data?.data.message);
    } else if (JBTConnect.isSuccess) {
      updateUser.mutate(
        {
          user: {
            installation_identifier: installationIdentifier,
          },
        },
        {
          onSuccess() {
            setJbtErrorOccurred(false);
            onClose();
            showToast(
              t("jbt.toastMessageSuccess.title"),
              JBTConnect.data?.data.message,
              ToastTypes.SUCCESS,
              true,
              true
            );
          },
        }
      );
    }
  }, [JBTConnect.isSuccess, JBTConnect.data]);

  const onSubmit = (formData: any) => {
    const authType = selectedOption === numberType.JBT ? "jbt_id" : "phone";
    const authKey = formData.JBTnumber;

    JBTConnect.mutate({
      auth_type: authType,
      auth_key: authKey,
      installation_identifier: installationIdentifier,
    });
  };
  useEffect(() => {
    if (!isOpen) {
      reset();
      setJbtErrorOccurred(false);
      setJbtErrorMsg("");
    }
  }, [isOpen]);

  return (
    <>
      <Modal
        headerTitle={t("jbt.title.text")}
        isOpen={isOpen}
        onClose={onClose}
        paddingLeft={0}
        paddingRight={0}
        paddingBottom={40}
        paddingTop={16}
      >
        <ScrollView keyboardShouldPersistTaps={"always"}>
          {jbt_id ? (
            <JBTConnectedModal jbtID={jbt_id} />
          ) : (
            <Container>
              <VDBText>{t("jbt.screenText.header.text")}</VDBText>
              <Spacer size={SpaceType.Basic} />

              <RadioButton
                label={t("jbt.radioLabel.jbt.text")}
                checked={selectedOption === numberType.JBT}
                onPress={() => setSelectedOption(numberType.JBT)}
                labelColor={theme["core-05"]}
              />
              <RadioButton
                label={t("jbt.radioLabel.phone.text")}
                checked={selectedOption === numberType.PHONE}
                onPress={() => setSelectedOption(numberType.PHONE)}
                labelColor={theme["core-06"]}
              />

              <Spacer size={SpaceType.Basic} />
              <Controller
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Input
                    key={selectedOption}
                    inputLabel={
                      selectedOption === numberType.JBT
                        ? t("jbt.input.inputLabel.jbt.text")
                        : t("jbt.input.inputLabel.phone.text")
                    }
                    placeholder={
                      selectedOption === numberType.JBT
                        ? t("jbt.input.placeholder.jbt.text")
                        : t("jbt.input.placeholder.phone.text")
                    }
                    hideIconContainer={false}
                    onBlur={onBlur}
                    value={value === undefined ? "" : `${value}`}
                    onChangeText={(text) => {
                      if (validNumberRegex.test(text)) {
                        onChange(text);
                        setJbtErrorOccurred(false);
                        setJbtErrorMsg("");
                      }
                    }}
                    secureTextEntry={false}
                    error={jbtErrorMsg}
                    keyboardType="phone-pad"
                  />
                )}
                name="JBTnumber"
              />
              <Spacer size={SpaceType.Basic} />
              {!jbtErrorOccurred &&
                (selectedOption === numberType.JBT ? (
                  <VDBText>
                    {t("jbt.screenText.inputHintMessage.jbt.text")}
                  </VDBText>
                ) : (
                  <VDBText>
                    {t("jbt.screenText.inputHintMessage.phone.text")}
                  </VDBText>
                ))}
              <Spacer size={SpaceType.Big} />
              <Button
                disabled={!isValid || jbtErrorOccurred || JBTConnect.isLoading}
                loading={JBTConnect.isLoading || updateUser.isLoading}
                category={ButtonCategory.default}
                type={ButtonType.primary}
                title={t("jbt.button.save.text")}
                onPress={handleSubmit(onSubmit)}
              />
              <Spacer size={SpaceType.Big} />
              <RenderHtml
                contentWidth={width}
                source={HtmlSource}
                tagsStyles={{
                  body: { ...typography[styleKey], color: theme["core-06"] },
                }}
              />
            </Container>
          )}
        </ScrollView>
      </Modal>
    </>
  );
};

export default JBTdropdown;
